/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useEffect } from 'react';
import { FullProject, Task, ViewMode } from '../types';
import { isEditableElement } from '../utils/commandManager';
import { canUserEditTask } from '../utils/projectPermissions';

export interface UseGlobalKeyboardShortcutsProps {
  setShowCommandPalette: React.Dispatch<React.SetStateAction<boolean>>;
  setShowExportProModal: (show: boolean) => void;
  setShowQuickAddTaskModal: (show: boolean) => void;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  closeTopModalOnEscape: () => boolean;
  isModalOpen: boolean;
  editingTask: Task | null;
  setEditingTask: (task: Task | null) => void;
  currentProjectId: string;
  deleteTask: (id: string | number) => void;
  updateTask: (id: string | number, updates: Partial<Task>, projectId?: string) => void;
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  activePoolTab?: string;
  activeMetricsTab?: string;
  undo?: () => void;
  redo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  isProjectEditor?: boolean;
  allProjects?: FullProject[];
  currentUserId?: string | null;
  userEmail?: string | null;
  canEditEditingTask?: boolean;
}

export function createGlobalKeyDownHandler({
  setShowCommandPalette,
  setShowExportProModal,
  setShowQuickAddTaskModal,
  setShowFilters,
  closeTopModalOnEscape,
  isModalOpen,
  editingTask,
  setEditingTask,
  currentProjectId,
  deleteTask,
  updateTask,
  viewMode,
  setViewMode,
  activePoolTab,
  activeMetricsTab,
  undo,
  redo,
  isProjectEditor = true,
  allProjects,
  currentUserId,
  userEmail,
  canEditEditingTask,
}: UseGlobalKeyboardShortcutsProps) {
  return (e: KeyboardEvent) => {
      // 1. ESC key ALWAYS closes open modals/cards/dropdowns even if user is typing in an input field
      if (e.key === 'Escape') {
        if (editingTask) {
          // Delegate Escape handling to useTaskEditForm so duplicate menus close first
          // and handleAutoSaveAndClose flushes pending drafts before closing.
          return;
        }
        if (closeTopModalOnEscape()) {
          e.preventDefault();
          return;
        }
      }

      // 2. Guard: Never intercept keyboard shortcuts when user is actively editing in an input, textarea, or contenteditable
      const target = e.target as HTMLElement | null;
      const isTyping = isEditableElement(target || document.activeElement) || (target && (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.tagName === 'SELECT' || 
        target.isContentEditable
      ));

      if (isTyping) return;

      const isMac = typeof window !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
      const isCmdOrCtrl = isMac ? e.metaKey : e.ctrlKey;

      // 3. Undo / Redo Shortcuts
      if (isCmdOrCtrl) {
        // Redo: Ctrl+Y (Windows) or Cmd+Shift+Z (Mac) or Ctrl+Shift+Z
        if (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z')) {
          e.preventDefault();
          redo?.();
          return;
        } else if (!e.shiftKey && e.key.toLowerCase() === 'z') {
          // Undo: Ctrl+Z (Windows) or Cmd+Z (Mac)
          e.preventDefault();
          undo?.();
          return;
        }
      }

      // Strg + K / Cmd + K always toggles Command Palette
      if (isCmdOrCtrl && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setShowCommandPalette(prev => !prev);
        return;
      }

      // Strg + P / Cmd + P toggles Multi-Project Export Pro
      if (isCmdOrCtrl && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setShowExportProModal(true);
        return;
      }

      // 4. Modal Check Guard - Do NOT trigger view switching or task creation when a modal or dialog is open
      // 5. STRICT WHITELIST GUARD: Shortcuts 1-9, N and F are ONLY allowed when the user is inside a valid project view tab!
      const VALID_PROJECT_VIEWS = [
        'gantt',
        'kanban',
        'list',
        'timeline',
        'calendar',
        'mindmap',
        'projectDocuments',
        'communication'
      ];

      // Exclude top-level views: metricsDashboard, projectPool, report (Fortschritt), aiAssistant, analysis, dashboard, ideaPool, annualReport
      const isNonProjectView = Boolean(
        !VALID_PROJECT_VIEWS.includes(viewMode) ||
        viewMode === 'metricsDashboard' ||
        viewMode === 'projectPool' ||
        viewMode === 'report' ||
        viewMode === 'aiAssistant' ||
        viewMode === 'analysis' ||
        viewMode === 'dashboard' ||
        (activePoolTab as string) === 'ideaPool' ||
        (activePoolTab as string) === 'annualReport' ||
        (activeMetricsTab as string) === 'annualReport' ||
        (activeMetricsTab as string) === 'ideaPool' ||
        (activeMetricsTab as string) === 'analysis'
      );

      const canUseProjectShortcuts = !isModalOpen && !isNonProjectView;

      const key = e.key.toLowerCase();

      // N: Öffnet das Schnellerstellungs-Modal für Aufgaben
      if (key === 'n' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!canUseProjectShortcuts) return;
        e.preventDefault();
        setShowQuickAddTaskModal(true);
        return;
      }

      // F: Filterleiste umschalten (NUR in echten Projekt-Ansichten!)
      if (key === 'f' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (!canUseProjectShortcuts) return;
        e.preventDefault();
        setShowFilters(prev => !prev);
        return;
      }

      // 1-9: Ansichten blitzschnell umschalten (NUR in echten Projekt-Ansichten!)
      if (!e.ctrlKey && !e.metaKey && !e.altKey && canUseProjectShortcuts) {
        if (e.key === '1') { e.preventDefault(); setViewMode('kanban'); return; }
        if (e.key === '2') { e.preventDefault(); setViewMode('gantt'); return; }
        if (e.key === '3') { e.preventDefault(); setViewMode('list'); return; }
        if (e.key === '4') { e.preventDefault(); setViewMode('timeline'); return; }
        if (e.key === '5') { e.preventDefault(); setViewMode('calendar'); return; }
        if (e.key === '6') { e.preventDefault(); setViewMode('mindmap'); return; }
        if (e.key === '7') { e.preventDefault(); setViewMode('projectDocuments'); return; }
        if (e.key === '8') { e.preventDefault(); setViewMode('communication'); return; }
        if (e.key === '9') { e.preventDefault(); setViewMode('metricsDashboard'); return; }
      }

      const canMutateEditingTask =
        canEditEditingTask !== undefined
          ? canEditEditingTask
          : isProjectEditor !== false &&
            (!editingTask ||
              !allProjects ||
              canUserEditTask(editingTask, allProjects, currentUserId || undefined, userEmail));

      // Delete / Backspace: Geöffnete Aufgabe löschen (nur mit Schreibberechtigung)
      if ((e.key === 'Delete' || e.key === 'Backspace') && editingTask) {
        if (!canMutateEditingTask) return;
        e.preventDefault();
        if (window.confirm(`Möchten Sie die Aufgabe "${editingTask.name}" wirklich löschen?`)) {
          deleteTask(editingTask.id);
          setEditingTask(null);
        }
        return;
      }

      // Space: Erledigt-Status umschalten bei geöffneter Aufgabe (nur mit Schreibberechtigung)
      if (e.key === ' ' && editingTask) {
        if (!canMutateEditingTask) return;
        e.preventDefault();
        const updated: Task = {
          ...editingTask,
          completed: !editingTask.completed,
          status: !editingTask.completed ? 'completed' : 'open'
        };
        updateTask(editingTask.id, updated, editingTask.projectId || currentProjectId);
        setEditingTask(updated);
        return;
      }
  };
}

export function useGlobalKeyboardShortcuts(props: UseGlobalKeyboardShortcutsProps) {
  useEffect(() => {
    const handleGlobalKeyDown = createGlobalKeyDownHandler(props);
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [props]);
}
